import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Coffee, ArrowLeft } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setMessage("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMessage(data.message || "If that email exists, a reset link has been sent.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-taupe-100">
      <form
        onSubmit={submit}
        className="w-full max-w-md card-soft p-8 sm:p-10 animate-fade-in-up"
        data-testid="forgot-password-form"
      >
        <div className="flex items-center gap-2 mb-6 text-espresso-800">
          <Coffee className="w-6 h-6" />
          <span className="font-heading font-bold text-lg">Brew Books</span>
        </div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-taupe-500 mb-2">
          Password recovery
        </div>
        <h2 className="font-heading font-extrabold text-3xl tracking-tighter text-espresso-900">
          Forgot your password?
        </h2>
        <p className="text-taupe-600 mt-2 text-sm">
          Enter your email and we&apos;ll send you a reset link.
        </p>

        <div className="mt-6 space-y-5">
          <div>
            <Label htmlFor="email" className="text-sm font-semibold text-espresso-800 mb-2 block">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@cafe.com"
              className="bg-taupe-100 border-taupe-200 rounded-xl h-12"
              data-testid="forgot-email-input"
            />
          </div>

          {message && (
            <div
              className="text-sm text-matcha-500 bg-matcha-100 border border-matcha-500/20 rounded-xl px-4 py-3"
              data-testid="forgot-success"
            >
              {message}
              <div className="text-xs text-taupe-600 mt-2">
                Since email delivery isn&apos;t configured, check the backend logs for the link.
              </div>
            </div>
          )}
          {error && (
            <div className="text-sm text-terracotta-500 bg-terracotta-100 border border-terracotta-500/20 rounded-xl px-4 py-3">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-12 rounded-xl bg-espresso-800 hover:bg-espresso-900 text-white font-semibold text-base"
            data-testid="forgot-submit-button"
          >
            {submitting ? "Sending…" : "Send reset link"}
          </Button>

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 text-sm text-taupe-600 hover:text-espresso-800 transition-colors"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
